'use client';

import PlaceIcon from '@mui/icons-material/Place';
import { PuzzleResultsState } from '@wt/app/game/_components/types';
import { GameImageElement, GameMapElement } from '@wt/app/game/_elements';
import { MobileRoundStepper } from '@wt/game/components/elements/round-progress/MobileRoundStepper';
import { useRoundProgress } from '@wt/game/providers/roundProgress/RoundProgressProvider';
import { Coordinate } from '@wt/shared/components/maps/mapkit/types';
import { YearSlider } from '@wt/shared/components/yearslider';
import { MapInteractionEvent } from '@wt/shared/libs/mapkit';
import { isNil } from 'lodash';
import { ChangeEvent, useEffect, useState } from 'react';

import { GameModeTag } from '../GameModeTag';
import { GuessButton } from '../GuessButton';
import { getSiteMode, hasHints } from '@wt/utilities/siteMode';
import { useHints } from '@wt/game/providers/hints/HintsProvider';
import { ProceedModal } from '../ProceedModal';

export enum EMobileGameView {
  image,
  map,
}

const HelpText = ({
  view,
  hasLocation,
  hasYear,
}: {
  view: EMobileGameView;
  hasLocation: boolean;
  hasYear: boolean;
}) => {
  const getHelpText = () => {
    if (getSiteMode() == 'wheretaken') {
      switch (view) {
        case EMobileGameView.image:
          return hasLocation
            ? 'Awesome! If you’re sure of your answer, you can press “GUESS” now!'
            : 'Explore the image and try to guess where it was taken. Then navigate to the map to place your guess.';
        case EMobileGameView.map:
          return hasLocation
            ? 'Awesome! If you’re sure of your answer, you can press “GUESS” now!'
            : 'Click anywhere on the map to place the pin.';
      }
    } else if (getSiteMode() == 'whentaken-movies') {
      switch (view) {
        case EMobileGameView.image:
          return hasLocation && hasYear ? (
            'Awesome! If you’re sure of your answers, you can press “GUESS” now!'
          ) : hasLocation ? (
            'Great job! Now move the slider to select the year or type in your answer.'
          ) : hasYear ? (
            'Great job! Now navigate to the map and place your pin.'
          ) : (
            <span>
              Explore the movie scene to determine the year and location in
              which it was <span className="italic">set</span>. Then navigate to
              the map and use the year slider to place your guesses.
            </span>
          );
        case EMobileGameView.map:
          return hasLocation && hasYear
            ? 'Awesome! If you’re sure of your answers, you can press “GUESS” now!'
            : hasLocation
              ? 'Great job! Now move the slider to select the year or type in your answer.'
              : 'Click anywhere on the map to place the pin.';
      }
    } else {
      switch (view) {
        case EMobileGameView.image:
          return hasLocation && hasYear
            ? 'Awesome! If you’re sure of your answers, you can press “GUESS” now!'
            : hasLocation
              ? 'Great job! Now move the slider to select the year or type in your answer.'
              : hasYear
                ? 'Great job! Now navigate to the map and place your pin.'
                : 'Explore the image and try to guess when and where it was taken. Then navigate to the map and use the year slider to place your guesses.';
        case EMobileGameView.map:
          return hasLocation && hasYear
            ? 'Awesome! If you’re sure of your answers, you can press “GUESS” now!'
            : hasLocation
              ? 'Great job! Now move the slider to select the year or type in your answer.'
              : 'Click anywhere on the map to place the pin.';
      }
    }
  };

  return (
    <div className="h-12 text-center text-xs flex items-center">
      {getHelpText()}
    </div>
  );
};

const RoundNumber = ({
  roundNumber,
  stepperExpanded,
  setStepperExpaned,
}: {
  roundNumber: number;
  stepperExpanded: boolean;
  setStepperExpaned: (e: boolean) => void;
}) => {
  return (
    <>
      {!stepperExpanded && (
        <div
          onClick={() => setStepperExpaned(true)}
          className="absolute left-4 top-4 z-20 flex size-9 shrink-0 items-center justify-center rounded-full bg-white font-semibold text-highlight dark:text-highlight-dark movies:text-primary-content shadow-lg"
        >
          {roundNumber}
        </div>
      )}
      {stepperExpanded && (
        <div className="absolute left-4 top-4 z-20">
          <MobileRoundStepper closeStepper={() => setStepperExpaned(false)} />
        </div>
      )}
    </>
  );
};

export const MapButton = ({
  hasLocation,
  isResults,
  onClick,
}: {
  hasLocation: boolean;
  isResults: boolean;
  onClick: () => void;
}) => {
  return (
    <div
      onClick={onClick}
      className="absolute bottom-4 right-4 z-20 flex size-16 cursor-pointer items-center justify-center overflow-hidden rounded-xl shadow-lg"
    >
      <img
        src={'/assets/images/small-map.png'}
        alt="Map Button"
        className="size-full object-cover object-center"
      />
      {!isResults && (
        <>
          <PlaceIcon
            fontSize="large"
            className="absolute inset-auto text-primary brightness-[.6] movies:text-accent movies:brightness-100"
          />
          {!hasLocation && (
            <div className="absolute right-[17px] top-[17px] z-30 size-2 rounded-full bg-error" />
          )}
        </>
      )}
      {isResults && (
        <>
          <PlaceIcon
            fontSize="large"
            className="absolute bottom-1 left-1 text-primary brightness-[0.6] movies:text-accent movies:brightness-100"
          />
          <PlaceIcon
            htmlColor="#000"
            fontSize="large"
            className="absolute right-1 top-1"
          />
        </>
      )}
    </div>
  );
};

export const ImageButton = ({
  src,
  onClick,
  blur,
}: {
  src: string;
  onClick: () => void;
  blur?: boolean;
}) => {
  return (
    <div
      onClick={onClick}
      className="absolute bottom-4 right-4 z-20 size-16 cursor-pointer items-center justify-center overflow-hidden rounded-xl shadow-lg"
    >
      <img
        src={src}
        alt="Image Button"
        className={`size-full bg-black object-cover object-center ${
          blur ? 'blur-sm' : ''
        }`}
      />
    </div>
  );
};

export const RoundGameMobile = ({
  results,
  selectedLocation,
  selectedLocationSecondary,
  handleLocation,
  handleLocationSecondary,
  year,
  handleYearChange,
  handleGuess,
}: {
  results: PuzzleResultsState;
  selectedLocation: Coordinate | undefined;
  selectedLocationSecondary: Coordinate | undefined;
  handleLocation: (e: MapInteractionEvent) => void;
  handleLocationSecondary: (e: MapInteractionEvent) => void;
  year: number | null;
  handleYearChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleGuess: () => void;
}) => {
  const [view, setView] = useState<EMobileGameView>(EMobileGameView.image);
  const [stepperExpanded, setStepperExpanded] = useState<boolean>(false);
  const { gameMode, currentRound } = useRoundProgress();
  const { trigger, setTrigger } = useHints();

  //blured image for game 2025-06-11 round 3
  const isDismissed =
    typeof window !== 'undefined' &&
    localStorage.getItem('proceedModalDismissed') === 'true';

  const shouldBlurImage =
    getSiteMode() === 'whentaken' &&
    !isDismissed &&
    results?.date === '2025-06-11' &&
    results?.round === 2;

  const [bluredImage, setBluredImage] = useState(shouldBlurImage);
  const [showProceedModal, setShowProceedModal] = useState<boolean>(false);

  useEffect(() => {
    setBluredImage(shouldBlurImage);
    setShowProceedModal(shouldBlurImage);
  }, [shouldBlurImage]);

  useEffect(() => {
    if (trigger == 'selectMap') {
      setTrigger('');
      setView(EMobileGameView.map);
    }
  }, [trigger, setTrigger, setView]);

  return (
    <div className="flex size-full flex-col items-center justify-between px-2 py-1">
      {gameMode != 'daily' && (
        <div className="mb-3 flex w-full items-center justify-center">
          <GameModeTag />
        </div>
      )}
      <div
        className={`relative ${getSiteMode() != 'wheretaken' ? 'aspect-[6/5]' : 'flex-1'} w-full`}
      >
        <div
          className={`absolute size-full overflow-hidden rounded-md bg-base-100 transition-all duration-500 ease-in-out ${view === EMobileGameView.image ? 'z-10 scale-100' : 'translate-x-full translate-y-full scale-0'}`}
        >
          <GameImageElement
            results={results}
            puzzle={results.puzzle[results.round]}
            mode={'game'}
            blur={bluredImage}
          />
        </div>
        <div
          className={`absolute size-full rounded-md transition-all duration-500 ease-in-out ${view === EMobileGameView.map ? 'z-10 scale-100' : 'translate-x-full translate-y-full scale-0'}`}
        >
          <GameMapElement
            puzzle={results.puzzle[results.round]}
            selectedLocation={selectedLocation}
            selectedLocationSecondary={selectedLocationSecondary}
            onLocationSelect={handleLocation}
            onLocationSelectSecondary={handleLocationSecondary}
            showHintButton={hasHints()}
          />
        </div>

        {view === EMobileGameView.image && (
          <MapButton
            hasLocation={!isNil(selectedLocation)}
            isResults={false}
            onClick={() => setView(EMobileGameView.map)}
          />
        )}
        {view === EMobileGameView.map && (
          <ImageButton
            src={results.puzzle[results.round].url}
            onClick={() => setView(EMobileGameView.image)}
            blur={bluredImage}
          />
        )}

        <RoundNumber
          roundNumber={currentRound}
          stepperExpanded={stepperExpanded}
          setStepperExpaned={setStepperExpanded}
        />
      </div>

      {getSiteMode() != 'wheretaken' && (
        <div className="mb-3 mt-6 flex w-full px-6">
          <YearSlider onChange={handleYearChange} selectedYear={year} />
        </div>
      )}

      <HelpText
        view={view}
        hasLocation={!isNil(selectedLocation)}
        hasYear={!isNil(year)}
      />

      <div className="min-h-[70px] shrink" />
      <div className="fixed bottom-0 left-0 z-[90] flex w-full bg-base-100 p-3">
        <GuessButton
          yearSelected={!isNil(year)}
          locationSelected={!isNil(selectedLocation)}
          secondaryLocationSelected={!isNil(selectedLocationSecondary)}
          onClick={handleGuess}
        />
      </div>

      <ProceedModal
        visible={showProceedModal}
        setShowModal={setShowProceedModal}
        setBluredImage={setBluredImage}
      />
    </div>
  );
};
